import {ConfigProvider, StaticHostConfig} from '@aboutyou/config';
import {GrpcConfig, ServerRenderingModeProvider} from '@aboutyou/data-fetcher';
import {FeatureFlagProvider, FeatureFlagSetup} from '@aboutyou/feature-flags';
import {HeadContext, HeadElementsProvider} from '@aboutyou/head-elements';
import {PageStateProvider} from '@aboutyou/history-page-state';
import {I18nProvider, StaticI18nConfig} from '@aboutyou/i18n';
import {HydrationProvider} from '@aboutyou/lazy-imports';
import {useIsAnyOverlayOpen} from '@aboutyou/opened-state-manager';
import {
  CanGoBackProvider,
  RouterProvider,
  RouterProviderProps,
  useCompleteLocation,
} from '@aboutyou/router';
import {StorageProvider, StorageState} from '@aboutyou/storage';
import {SERVER_RENDERING_MODE} from '@aboutyou/utils/server-rendering-modes';
import React, {useRef} from 'react';
import {GrpcClientHostConfigProvider} from './providers/GrpcClientHostConfigProvider';
import {TrackingProvider} from './providers/TrackingProvider';

interface FeaturesProviderProps {
  headContext: HeadContext;
  i18nConfig: StaticI18nConfig;
  hostConfig: StaticHostConfig;
  routerConfig: RouterProviderProps;
  storage: StorageState;
  grpcConfig: GrpcConfig;
  featureFlagSetup: FeatureFlagSetup;
  documentReferrer: string;
  serverRenderingMode: SERVER_RENDERING_MODE;
  disableTranslationLoader?: boolean;
}

export const FeaturesProvider: React.FC<
  React.PropsWithChildren<FeaturesProviderProps>
> = ({
  children,
  headContext,
  i18nConfig,
  hostConfig,
  routerConfig,
  grpcConfig,
  featureFlagSetup,
  storage,
  documentReferrer,
  serverRenderingMode,
  disableTranslationLoader,
}) => (
  <HydrationProvider>
    <ServerRenderingModeProvider value={serverRenderingMode}>
      <RouterProvider {...routerConfig}>
        <StorageProvider {...storage}>
          <ConfigProvider config={hostConfig}>
            <I18nProvider
              {...i18nConfig}
              disableTranslationLoader={disableTranslationLoader}
            >
              <FeatureFlagProvider {...featureFlagSetup}>
                <TrackingProvider documentReferrer={documentReferrer}>
                  <GrpcClientHostConfigProvider config={grpcConfig}>
                    <HeadElementsProvider headContext={headContext}>
                      <PageStateProviderWithKey>
                        <CanGoBackProvider>{children}</CanGoBackProvider>
                      </PageStateProviderWithKey>
                    </HeadElementsProvider>
                  </GrpcClientHostConfigProvider>
                </TrackingProvider>
              </FeatureFlagProvider>
            </I18nProvider>
          </ConfigProvider>
        </StorageProvider>
      </RouterProvider>
    </ServerRenderingModeProvider>
  </HydrationProvider>
);

export const PageStateProviderWithKey: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const location = useCompleteLocation();
  const pageKeyRef = useRef(location.key);

  // ASFE-11903: remembers which bucket each overlay history entry was frozen
  // to, so returning to it after a real page navigation (e.g. coins history →
  // ODP → back) restores the underlying page's bucket instead of adopting the
  // key of the page navigated away to
  const bucketByLocationKey = useRef(
    new Map<string | undefined, string | undefined>(),
  );

  // ASFE-13047: freeze the page-state bucket while any overlay is open so
  // `useHistoryPageState` consumers (e.g. the product stream's pages
  // array) survive the overlay's history mutations. The previous
  // `useIsLastInteractionUIPushEvent` signal depended on a `lastAction`
  // ref updated asynchronously inside `history.listen`; it misfired on
  // nested overlay opens and on closes that go through `replaceUiUpdate`
  // (which strips `PUSH_UI_STATE` but preserves the still-open parent
  // overlay's key in state).
  const isAnyOverlayOpen = useIsAnyOverlayOpen();

  if (!isAnyOverlayOpen) {
    pageKeyRef.current = location.key;
    bucketByLocationKey.current.delete(location.key);
  } else if (bucketByLocationKey.current.has(location.key)) {
    pageKeyRef.current = bucketByLocationKey.current.get(location.key);
  } else {
    bucketByLocationKey.current.set(location.key, pageKeyRef.current);
  }

  return (
    <PageStateProvider pageKey={pageKeyRef.current}>
      {children}
    </PageStateProvider>
  );
};
